package fyi.moko.hotpl.domain.service

import fyi.moko.hotpl.domain.model.Friend
import fyi.moko.hotpl.domain.model.FriendSharedPlace
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.domain.model.isWantToGo
import fyi.moko.hotpl.domain.model.normalizeForComparison

/**
 * A place both I and at least one friend want to go to (backlog 0-37, stage 1). [friends]
 * is every friend whose shared WISHLIST place matched — one card per place, not per friend.
 */
data class MutualWish(
    val place: FriendSharedPlace,
    val friends: List<Friend>,
)

/**
 * Identity of "the same real place" for the mutual-wish match. Same shape as [DuplicateKey]
 * (normalized name + exact coordinates) with one relaxation: two places that **both** lack
 * coordinates match on name alone. [DuplicateKey] refuses that because account-merge dedup
 * rewrites ids and a false positive there loses data; here a false positive only shows a
 * card. The relaxation exists for a real case — a friend's place made without coordinates
 * (Cafe Concerto, 2026-09-18) that I saved through "Add to Want to go", which copies the
 * null coordinates verbatim. One side with coordinates and the other without never match.
 */
data class MutualWishKey(val name: String, val latitude: Double?, val longitude: Double?)

fun mutualWishKeyOf(name: String, latitude: Double?, longitude: Double?): MutualWishKey? {
    if ((latitude == null) != (longitude == null)) return null
    val normalized = normalizeForComparison(name)
    if (normalized.isEmpty()) return null
    return MutualWishKey(normalized, latitude, longitude)
}

/**
 * Places that appear as WISHLIST in a friend's shared list *and* count as Want to go in my
 * journal ([isWantToGo], the same rule as the Journal chip and the hollow map pin). Only
 * friends whose shared places are already in [sharedPlaces] contribute — a friend not yet
 * loaded simply adds nothing. Ordered by name; friends within a card in [friends] order.
 */
fun findMutualWishes(
    friends: List<Friend>,
    sharedPlaces: Map<String, List<FriendSharedPlace>>,
    myPlaces: List<Place>,
    myVisitCounts: Map<String, Int>,
): List<MutualWish> {
    val myWishKeys = myPlaces
        .filter { it.isWantToGo(myVisitCounts[it.id] ?: 0) }
        .mapNotNullTo(HashSet()) { mutualWishKeyOf(it.name, it.latitude, it.longitude) }
    if (myWishKeys.isEmpty()) return emptyList()

    return friendWishEntries(friends, sharedPlaces)
        .filterKeys { it in myWishKeys }
        .values
        .map { matches -> MutualWish(place = matches.first().first, friends = matches.map { it.second }) }
        .sortedBy { it.place.name.lowercase() }
}

/**
 * Every WISHLIST place in the friends' shared lists, keyed — the friend side of the match
 * on its own, so Home and Detail can look up "who else wants to go here" per place of mine
 * ([friendsWhoAlsoWantToGo]). A friend appears at most once per key.
 */
fun friendWishKeys(
    friends: List<Friend>,
    sharedPlaces: Map<String, List<FriendSharedPlace>>,
): Map<MutualWishKey, List<Friend>> =
    friendWishEntries(friends, sharedPlaces).mapValues { (_, matches) -> matches.map { it.second }.distinct() }

/** The friends who want to go to [place] of mine — empty unless the place is Want to go on my side too. */
fun friendsWhoAlsoWantToGo(
    place: Place,
    visitCount: Int,
    friendWishes: Map<MutualWishKey, List<Friend>>,
): List<Friend> {
    if (friendWishes.isEmpty() || !place.isWantToGo(visitCount)) return emptyList()
    val key = mutualWishKeyOf(place.name, place.latitude, place.longitude) ?: return emptyList()
    return friendWishes[key].orEmpty()
}

private fun friendWishEntries(
    friends: List<Friend>,
    sharedPlaces: Map<String, List<FriendSharedPlace>>,
): Map<MutualWishKey, List<Pair<FriendSharedPlace, Friend>>> {
    val byKey = LinkedHashMap<MutualWishKey, MutableList<Pair<FriendSharedPlace, Friend>>>()
    for (friend in friends) {
        for (place in sharedPlaces[friend.uid].orEmpty()) {
            if (place.status != PlaceStatus.WISHLIST) continue
            val key = mutualWishKeyOf(place.name, place.latitude, place.longitude) ?: continue
            byKey.getOrPut(key) { mutableListOf() } += place to friend
        }
    }
    return byKey
}
